import { forwardRef, useRef } from "react";

import { MenuItem as MuiMenuItem, MenuItemProps as MuiMenuItemProps, useTheme } from "@mui/material";

import { mergeSx } from "@/common/helpers/mergeSx";
import { FormTypography } from "@/forms-design-system/FormTypography";

export type FormSelectItemProps = Omit<MuiMenuItemProps, "disableRipple"> & {
  "data-test"?: string;
};

export const FormSelectItem = forwardRef<HTMLLIElement, FormSelectItemProps>(
  ({ sx, children, selected, "data-test": dataTest, onTouchStart, onTouchMove, onTouchEnd, ...rest }, ref) => {
    const theme = useTheme();
    const touchMovedRef = useRef(false);

    const handleTouchStart = (event: React.TouchEvent<HTMLLIElement>) => {
      touchMovedRef.current = false;
      onTouchStart?.(event);
    };

    const handleTouchMove = (event: React.TouchEvent<HTMLLIElement>) => {
      touchMovedRef.current = true;
      onTouchMove?.(event);
    };

    const handleTouchEnd = (event: React.TouchEvent<HTMLLIElement>) => {
      onTouchEnd?.(event);
      if (touchMovedRef.current || event.defaultPrevented || !event.cancelable) {
        return;
      }
      event.preventDefault();
      event.currentTarget.click();
    };

    const styles = mergeSx(
      {
        width: "100%",
        ...theme.typography.body2,
        color: theme.palette.formsComponents.selectField.itemTextColor,
        transition: "background-color 100ms linear, color 100ms linear",
        minHeight: 0, // Override default MUI style
        ["&.Mui-selected"]: {
          ...theme.typography.body2,
          backgroundColor: theme.palette.formsComponents.selectField.selectedItemBackgroundColor,
          color: theme.palette.formsComponents.selectField.selectedItemTextColor,
          ["&:hover"]: {
            backgroundColor: theme.palette.formsComponents.selectField.itemHoverBackgroundColor,
            color: theme.palette.formsComponents.selectField.itemTextColor,
          },
        },
        ["&.Mui-selected.Mui-focusVisible"]: {
          backgroundColor: theme.palette.formsComponents.selectField.itemHoverBackgroundColor,
          color: theme.palette.formsComponents.selectField.itemTextColor,
        },
        ["&.Mui-focusVisible"]: {
          backgroundColor: theme.palette.formsComponents.selectField.itemHoverBackgroundColor,
          color: theme.palette.formsComponents.selectField.itemTextColor,
        },
        ["&:hover"]: {
          backgroundColor: theme.palette.formsComponents.selectField.itemHoverBackgroundColor,
          color: theme.palette.formsComponents.selectField.itemTextColor,
        },
        ["&.Mui-disabled"]: {
          opacity: 0.4,
        },
      },
      sx
    );

    return (
      <MuiMenuItem
        ref={ref}
        sx={styles}
        selected={selected}
        data-test={dataTest}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        {...rest}
        disableRipple
      >
        <FormTypography variant={selected ? "subtitle2" : "body2"} sx={{ width: "100%" }}>
          {children}
        </FormTypography>
      </MuiMenuItem>
    );
  }
);

FormSelectItem.displayName = "FormSelectItem";
